import { useQuery } from '@tanstack/react-query'
import type { Query, QueryClient } from '@tanstack/react-query'
import { getOrganizationDashboard } from '@/api/services/dashboard.service'

export const dashboardKeys = {
  organization: ['dashboard', 'organization'] as const,
  /**
   * Param-less prefix of every company dashboard query. The full key is
   * `companiesKeys.dashboard(id)` = ['companies', 'detail', id, 'dashboard'];
   * it is mirrored here (not imported) because useCompanies imports this file.
   */
  companyDetailPrefix: ['companies', 'detail'] as const,
}

/** True for `companiesKeys.dashboard(id)` queries, whatever the id. */
function isCompanyDashboardQuery(query: Pick<Query, 'queryKey'>): boolean {
  const key = query.queryKey
  return key[0] === 'companies' && key[1] === 'detail' && key[3] === 'dashboard'
}

/**
 * Refreshes the org dashboard and every company dashboard. Call after anything
 * that moves today's numbers: leave/correction decisions, shift changes,
 * realtime attendance events.
 */
export function invalidateDashboards(queryClient: QueryClient): Promise<void> {
  // Don't abort a dashboard fetch already in flight: it's heavy, and a burst
  // of events would otherwise keep restarting it.
  const options = { cancelRefetch: false }
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: dashboardKeys.organization }, options),
    queryClient.invalidateQueries(
      { queryKey: dashboardKeys.companyDetailPrefix, predicate: isCompanyDashboardQuery },
      options
    ),
  ]).then(() => undefined)
}

export function useDashboardStats(options?: { enabled?: boolean; date?: string | null }) {
  const date = options?.date ?? null
  return useQuery({
    // Prefix stays dashboardKeys.organization so invalidateDashboards() covers every date.
    queryKey: [...dashboardKeys.organization, date ?? 'live'],
    queryFn: () => getOrganizationDashboard(date),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    // "Today" and "clocked in now" go stale on their own; a past day doesn't.
    refetchInterval: date ? false : 60_000,
    placeholderData: (previous) => previous,
    enabled: options?.enabled !== false,
  })
}
